import React, { FC } from 'react'
import { Box, Image, Link } from '@moonpig/launchpad-components'
import { styled, breakpoint } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import type { CollectionTileData } from './types'
import { IconSystemArrowForward } from '@moonpig/launchpad-assets'

type Props = {
  tile: CollectionTileData
  onClick: (tile: CollectionTileData) => void
}

const TILE_HEIGHT_MOBILE = '124px'
const TILE_HEIGHT_DESKTOP = '148px'
const IMAGE_CONTAINER_SIZE_MOBILE = '108px'
const IMAGE_CONTAINER_SIZE_DESKTOP = '132px'
const TILE_IMAGE_SIZE = 400
const TILE_PREFIX = 'For the'

const StyledLink = styled(Link)`
  display: block;
  min-width: 0;
  height: 100%;
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus {
    text-decoration: none;
  }
`

const StyledArrow = styled(Box)`
  ${s({
    width: 44,
    height: 44,
    display: { xs: 'none', md: 'flex' },
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    p: 4,
    ml: 4,
    border: 1,
    borderColor: 'colorBorder03',
    bgcolor: 'colorBackground01',
    visibility: 'hidden',
  })}
`

const StyledTile = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;
  min-height: 0;
  transform: translateY(0);
  transition: all 200ms ease-in-out;
  ${s({
    height: {
      xs: TILE_HEIGHT_MOBILE,
      lg: TILE_HEIGHT_DESKTOP,
    },
    p: 4,
    borderRadius: 2,
    border: 1,
    borderColor: 'colorBorder03',
  })}

  ${breakpoint('md')} {
    &:hover ${StyledArrow} {
      visibility: visible;
    }
    &:active {
      transform: scale(0.95);
    }
  }
`

const StyledImageContainer = styled(Box)`
  flex-shrink: 0;
  overflow: hidden;
  align-self: center;
  ${s({
    width: {
      xs: IMAGE_CONTAINER_SIZE_MOBILE,
      lg: IMAGE_CONTAINER_SIZE_DESKTOP,
    },
    height: {
      xs: IMAGE_CONTAINER_SIZE_MOBILE,
      lg: IMAGE_CONTAINER_SIZE_DESKTOP,
    },
    borderRadius: 1,
  })}
`

const StyledImage = styled(Image)`
  display: block;
  object-fit: cover;
  width: 100%;
  height: 100%;
`

const StyledPrefix = styled.span`
  display: block;
  ${s({
    typography: {
      xs: 'typeMobileBodyText',
      lg: 'typeDesktopBodyText',
    },
    fontWeight: 400,
    color: 'colorTextHeadline',
  })}
`

const StyledTitle = styled.span`
  display: block;
  ${s({
    typography: {
      xs: 'typeMobileDisplay05',
      lg: 'typeDesktopDisplay05',
    },
    fontWeight: 700,
    color: 'colorTextHeadline',
  })}
`

export const CollectionTile: FC<Props> = ({ tile, onClick }) => (
  <StyledLink href={tile.path} onClick={() => onClick(tile)}>
    <StyledTile bgcolor={tile.backgroundColor}>
      <StyledImageContainer>
        <StyledImage
          src={{ default: tile.image, webp: tile.image }}
          width={TILE_IMAGE_SIZE}
          height={TILE_IMAGE_SIZE}
          aria-hidden
          alt={tile.title}
        />
      </StyledImageContainer>
      <Box flex={1} minWidth={0} ml={6}>
        <StyledPrefix>{TILE_PREFIX}</StyledPrefix>
        <StyledTitle>{tile.title}</StyledTitle>
      </Box>
      <StyledArrow>
        <IconSystemArrowForward />
      </StyledArrow>
    </StyledTile>
  </StyledLink>
)
